'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X } from 'lucide-react';

const navLinks = [
  { label: 'About', href: '/about' },
  { label: 'How It Works', href: '/how-it-works' },
  { label: 'Who We Serve', href: '/who-we-serve' },
  { label: 'Why Us', href: '/why-operator-circle' },
  { label: 'FAQs', href: '/#faqs' },
  { label: 'Contact', href: '/contact' },
];

export default function PublicNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    // Set initial state
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#0B1F3A] shadow-lg shadow-black/20'
          : 'bg-[#0B1F3A]/90 backdrop-blur-md'
      }`}
      style={{ borderBottom: scrolled ? '1px solid rgba(255,255,255,0.08)' : '1px solid transparent' }}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo — always visible */}
        <Link href="/" className="flex items-center gap-3 flex-shrink-0">
          <AppLogo src="/assets/images/opclogo-1784542402159.png" size={32} />
          <span className="text-white font-bold text-sm tracking-tight leading-tight hidden sm:block">
            Operator<br />Circle
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-6">
          {navLinks?.map(link => (
            <Link
              key={link?.label}
              href={link?.href}
              className={`text-sm font-medium transition-colors ${
                pathname === link?.href ? 'text-white' : 'text-white/70 hover:text-white'
              }`}
            >
              {link?.label}
            </Link>
          ))}
        </div>

        {/* CTAs */}
        <div className="hidden lg:flex items-center gap-3">
          <Link
            href="/request-access-page"
            className="text-sm font-semibold text-white/80 hover:text-white transition-colors px-4 py-2 border border-white/25 rounded-lg hover:border-white/50"
          >
            Request Access
          </Link>
          <Link
            href="/sign-in-login-screen"
            className="text-sm font-semibold bg-white text-[#0B1F3A] px-5 py-2.5 rounded-lg hover:bg-white/90 transition-colors"
          >
            Access Portal
          </Link>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-[#0B1F3A] border-t border-white/10 px-6 py-4">
          <div className="flex flex-col gap-1">
            {navLinks?.map(link => (
              <Link
                key={link?.label}
                href={link?.href}
                onClick={() => setMobileOpen(false)}
                className="text-sm font-medium text-white/70 hover:text-white py-2.5 px-3 rounded-lg hover:bg-white/8 transition-colors"
              >
                {link?.label}
              </Link>
            ))}
            <div className="flex flex-col gap-2 pt-3 mt-2 border-t border-white/10">
              <Link
                href="/request-access-page"
                onClick={() => setMobileOpen(false)}
                className="text-sm font-semibold text-white/80 hover:text-white py-2.5 px-4 border border-white/25 rounded-lg text-center transition-colors"
              >
                Request Access
              </Link>
              <Link
                href="/sign-in-login-screen"
                onClick={() => setMobileOpen(false)}
                className="text-sm font-semibold bg-white text-[#0B1F3A] py-2.5 px-4 rounded-lg text-center hover:bg-white/90 transition-colors"
              >
                Access Portal
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}